import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  ArrowLeft, Users, TrendingUp, Clock, Check, ShoppingCart,
  Instagram, Linkedin, Twitter, Facebook, Youtube, Share2,
  ExternalLink, Heart, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platformConfig = {
  instagram: { icon: Instagram, color: 'from-purple-600 to-pink-500', bg: 'bg-gradient-to-br from-purple-600 to-pink-500' },
  facebook: { icon: Facebook, color: 'bg-[#1877f2]', bg: 'bg-[#1877f2]' },
  twitter: { icon: Twitter, color: 'bg-black', bg: 'bg-black' },
  linkedin: { icon: Linkedin, color: 'bg-[#0077b5]', bg: 'bg-[#0077b5]' },
  youtube: { icon: Youtube, color: 'bg-[#ff0000]', bg: 'bg-[#ff0000]' },
};

const postTypeLabels = {
  feed_post: 'Feed Post',
  story: 'Story (24h)',
  reel: 'Reel',
  carousel: 'Carousel',
  short: 'YouTube Short',
  video: 'Video',
};

const formatFollowers = (count) => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count;
};

const SocialPageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPageDetails();
  }, [id]);

  const fetchPageDetails = async () => {
    try {
      const response = await axios.get(`${API}/social-pages/${id}`);
      setPage(response.data);
      if (response.data.packages && response.data.packages.length > 0) {
        setSelectedPackage(response.data.packages[0]);
      }
    } catch (error) {
      console.error('Error fetching social page:', error);
      toast.error('Failed to load social page details');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    if (!selectedPackage) {
      toast.error('Please select a package');
      return;
    }
    navigate('/checkout', { 
      state: { 
        itemType: 'social_page',
        socialPage: page, 
        package: selectedPackage 
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 text-center">
          <Share2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="font-playfair text-2xl font-bold mb-2">Social Page Not Found</h1>
          <p className="text-[#737373] mb-6">The social page you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/marketplace?type=social_media')}
            className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] rounded-lg hover:bg-[#B8960F]"
          >
            Browse Social Pages
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const platform = platformConfig[page.platform] || platformConfig.instagram;
  const PlatformIcon = platform.icon;

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="social-page-detail">
      <Helmet>
        <title>{page.name} - Social Media Promotion | BusinessMint PR</title>
        <meta name="description" content={`Get featured on ${page.name} (@${page.handle}) with ${formatFollowers(page.followers)} followers. ${page.description || ''}`} />
      </Helmet>
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/marketplace?type=social_media')}
          className="flex items-center gap-2 text-[#737373] hover:text-[#1a1a1a] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-manrope text-sm">Back to Social Media</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Page Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-8">
              <div className="flex items-start gap-6">
                {/* Logo/Avatar */}
                <div className={`w-24 h-24 rounded-full ${platform.bg} flex items-center justify-center flex-shrink-0`}>
                  {page.logo ? (
                    <img src={page.logo} alt={page.name} className="w-24 h-24 rounded-full object-cover" />
                  ) : (
                    <PlatformIcon className="w-12 h-12 text-white" />
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${platform.bg}`}>
                      {page.platform?.charAt(0).toUpperCase() + page.platform?.slice(1)}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 capitalize">
                      {page.page_type?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <h1 className="font-playfair text-3xl font-bold text-[#1a1a1a] mb-1">{page.name}</h1>
                  <p className="text-[#737373] font-manrope">@{page.handle}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mt-8 pt-6 border-t border-[#e5e5e5]">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-[#fee2e2] rounded-lg">
                    <Users className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="font-mono text-2xl font-bold text-[#1a1a1a]">{formatFollowers(page.followers)}</p>
                    <p className="text-xs text-[#737373] uppercase tracking-widest">Followers</p>
                  </div>
                </div>
                {page.engagement_rate && (
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-[#dcfce7] rounded-lg">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-mono text-2xl font-bold text-[#1a1a1a]">{page.engagement_rate}%</p>
                      <p className="text-xs text-[#737373] uppercase tracking-widest">Engagement</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {page.description && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">About</h2>
                <p className="font-manrope text-[#737373] leading-relaxed">{page.description}</p>
              </div>
            )}

            {/* Categories */}
            {page.categories && page.categories.length > 0 && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Categories</h2>
                <div className="flex flex-wrap gap-2">
                  {page.categories.map((cat, idx) => (
                    <span key={idx} className="px-4 py-2 bg-gray-100 text-[#1a1a1a] rounded-full text-sm font-manrope">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Available Post Types */}
            {page.post_types_available && page.post_types_available.length > 0 && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Available Post Types</h2>
                <div className="flex flex-wrap gap-3">
                  {page.post_types_available.map((type, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-[#f5f5f5] rounded-lg">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="font-manrope text-sm">{postTypeLabels[type] || type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Packages & Booking */}
          <div className="space-y-6">
            {/* Packages */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-6 sticky top-6">
              <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Select Package</h2>
              
              {page.packages && page.packages.length > 0 ? (
                <div className="space-y-3 mb-6">
                  {page.packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackage(pkg)}
                      data-testid={`package-${pkg.id}`}
                      className={`w-full p-4 border rounded-xl text-left transition-all ${
                        selectedPackage?.id === pkg.id
                          ? 'border-[#D4AF37] bg-[#fee2e2]/30 ring-1 ring-[#D4AF37]'
                          : 'border-[#e5e5e5] hover:border-[#D4AF37]'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-[#1a1a1a]">{pkg.name}</p>
                          <p className="text-xs text-[#737373] capitalize">{postTypeLabels[pkg.post_type] || pkg.post_type}</p>
                        </div>
                        <p className="font-mono font-bold text-lg text-[#D4AF37]">
                          ₹{pkg.price?.toLocaleString('en-IN')} <span className="text-xs font-normal">+GST</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-[#737373]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {pkg.turnaround_days} days
                        </span>
                        {pkg.duration_hours && (
                          <span>Duration: {pkg.duration_hours}h</span>
                        )}
                      </div>
                      {pkg.description && (
                        <p className="text-xs text-[#737373] mt-2">{pkg.description}</p>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[#737373] text-sm mb-6">No packages available. Contact us for custom pricing.</p>
              )}

              {/* Selected Package Summary */}
              {selectedPackage && (
                <div className="border-t border-[#e5e5e5] pt-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[#737373]">Selected:</span>
                    <span className="font-semibold">{selectedPackage.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#737373]">Base Price:</span>
                    <span className="font-mono text-2xl font-bold text-[#D4AF37]">
                      ₹{selectedPackage.price?.toLocaleString('en-IN')} <span className="text-xs font-normal">+GST</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Buy Button */}
              <button
                onClick={handleBuyNow}
                disabled={!selectedPackage}
                data-testid="buy-now-btn"
                className="w-full py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#B8960F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-xl flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Book Now
              </button>

              {/* Inclusions */}
              <div className="mt-6 pt-4 border-t border-[#e5e5e5]">
                <p className="text-xs text-[#737373] uppercase tracking-widest mb-3">Included:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                    <Check className="w-4 h-4 text-green-500" />
                    Caption writing
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                    <Check className="w-4 h-4 text-green-500" />
                    Relevant hashtags
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                    <Check className="w-4 h-4 text-green-500" />
                    Post scheduling
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                    <Check className="w-4 h-4 text-green-500" />
                    Performance screenshot
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SocialPageDetail;
